import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Account, MenuItems, Pagination, Response } from '@ui/src';
import {
  AccountsStats,
  Category,
  AccountsReportResponse,
  AccountsReport,
} from '../../types';
import { accountsReportApi } from '../service';

export interface AccountsReportState {
  menuItems?: MenuItems[];
  report?: AccountsReport[];
  reportPagination?: Pagination;
  accounts?: Account[];
  errorsList?: string;
}

const initialState: AccountsReportState = {
  menuItems: undefined,
  report: undefined,
};

export const accountsReportSlice = createSlice({
  name: 'accountsReport',
  initialState,
  reducers: {
    clearAccountsReportState: (state) => {
      state.report = undefined;
      state.accounts = undefined;
      state.reportPagination = undefined;
      state.errorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        accountsReportApi.endpoints.getCategories.matchFulfilled,
        (state, action: PayloadAction<Response<Category>>) => {
          const nextMenuItems = action.payload.data.edges.map((item) => ({
            category: {
              label: item.node.name,
              counter: '',
              id: item.node.id,
              number: item.node.number,
              groups: item.node.accountGroups?.map((group) => ({
                label: group.description,
                counter: '',
                id: group.id,
                number: group.number,
                accounts: group.primaryAccounts?.map((account) => ({
                  label: account.description,
                  counter: '',
                  id: account.id,
                  number: account.number,
                })),
              })),
            },
          }));

          state.menuItems = nextMenuItems as MenuItems[];
        },
      )
      .addMatcher(
        accountsReportApi.endpoints.getReportsAccountsStats.matchFulfilled,
        (state, action: PayloadAction<{ data: AccountsStats[] }>) => {
          if (state.menuItems) {
            const nextMenuItems = state.menuItems.map((menuItem) => {
              const result = { ...menuItem };
              const currentCategory = action.payload.data?.find(
                (item) => item.id === menuItem.category.id,
              );

              if (currentCategory) {
                result.category.counter = currentCategory.count.toString();

                result.category.groups = result.category.groups?.map(
                  (group) => {
                    const groupResult = { ...group };
                    const currentGroup = currentCategory.accountGroups.find(
                      (item) => item.id === group.id,
                    );

                    if (currentGroup) {
                      groupResult.counter = currentGroup.count.toString();

                      groupResult.accounts = groupResult.accounts?.map(
                        (primary) => {
                          const accountsResult = { ...primary };
                          const currentAccount =
                            currentGroup.primaryAccounts.find(
                              (item) => item.id === primary.id,
                            );

                          if (currentAccount) {
                            accountsResult.counter =
                              currentAccount.count.toString();
                          }

                          return accountsResult;
                        },
                      );
                    }

                    return groupResult;
                  },
                );
              }

              return result;
            });

            state.menuItems = nextMenuItems;
          }
        },
      )
      .addMatcher(
        accountsReportApi.endpoints.getBalanceSheetAccounts.matchPending,
        (state) => {
          state.errorsList = undefined;
        },
      )
      .addMatcher(
        accountsReportApi.endpoints.getBalanceSheetAccounts.matchRejected,
        (state, action: any) => {
          state.accounts = undefined;
          state.reportPagination = undefined;
          state.report = undefined;

          if (action?.payload.data) {
            if (Array.isArray(action?.payload.data?.message)) {
              state.errorsList = action?.payload.data?.message
                ?.map((item: { value: string }) => item.value)
                .join('; ');
            } else {
              state.errorsList = action?.payload.data?.error;
            }
          }
        },
      )
      .addMatcher(
        accountsReportApi.endpoints.getBalanceSheetAccounts.matchFulfilled,
        (state, action: PayloadAction<Response<Account>>) => {
          state.accounts = action.payload.data?.edges?.map((item) => item.node);

          state.reportPagination = action.payload.data.pageInfo;

          if (!action.payload?.data?.edges?.length) {
            state.report = [];
          }
        },
      )

      .addMatcher(
        accountsReportApi.endpoints.getAccountsReport.matchFulfilled,
        (state, action: PayloadAction<{ data: AccountsReportResponse }>) => {
          if (state.accounts) {
            const currentAccountsReport = state?.accounts?.map((item) => {
              const currentAccount = action?.payload?.data?.items?.find(
                (stat) => stat.accountId === item.id,
              );

              const accountReport = {
                id: item.id,
                accountId: currentAccount?.accountId,
                categoryId: currentAccount?.categoryId,
                creditIn: currentAccount?.creditIn,
                creditOut: currentAccount?.creditOut,
                creditTo: currentAccount?.creditTo,
                debitIn: currentAccount?.debitIn,
                debitOut: currentAccount?.debitOut,
                debitTo: currentAccount?.debitTo,
                groupId: currentAccount?.groupId,
                primaryId: currentAccount?.primaryId,
                accountType: item?.accountType,
                search: item?.description,
                closed: item.closed,
                fullNumber: item?.fullNumber,
                url: `/accounts/statements?account=${item?.fullNumber}`,
              };

              return accountReport;
            });

            state.report = currentAccountsReport;
          }
        },
      );
  },
});

export const { clearAccountsReportState } = accountsReportSlice.actions;

export default accountsReportSlice.reducer;
